import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Hint, HintType } from '@sparx/api/apis/sparx/hints/v1/hints';
import { Button } from '@sparx/sparx-design/components';
import dialogStyles from '@sparx/sparx-design/shared-styles/Dialog.module.css';
import classNames from 'classnames';
import { Video } from 'components/video/Video';
import Bearings3DigitSupport from 'images/hints/Bearings3DigitSupport.png';
import Money2dpSupport from 'images/hints/Money2dpSupport.png';
import { getImageURL, makeQuestionVideoUrls, VideoUrls } from 'utils/urls';

import styles from './HintModal.module.css';

const FRACTION_HINT_VIDEO_ID = 'ec27a003-1489-4000-9214-e5878eaa1049';
const fractionHintVideoUrls = makeQuestionVideoUrls(FRACTION_HINT_VIDEO_ID);

type HintModalProps = {
  hint: Hint | undefined;
  onOpenChange: (open: boolean) => void;
};

export const HintModal = ({ hint, onOpenChange }: HintModalProps) => {
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={!!hint}>
      <Dialog.Portal>
        <Dialog.Overlay className={dialogStyles.DialogOverlay} />
        <Dialog.Content
          className={classNames(
            dialogStyles.DialogContent,
            dialogStyles.FullWidth,
            dialogStyles.WithZIndex,
            dialogStyles.ContentHeight,
          )}
        >
          <HintModalContent hint={hint} />
          <div className={dialogStyles.DialogButtonsContainer}>
            <Button as={Dialog.Close}>Fix your answer</Button>
          </div>
          <Dialog.Close asChild>
            <button className={dialogStyles.IconButton}>
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

type HintModalContentProps = {
  hint: Hint | undefined;
};

const HintModalContent = ({ hint }: HintModalContentProps) => {
  const support = hint?.targetedSupport?.additionalSupport;
  switch (hint?.hintType) {
    case HintType.HINT_SIMPLIFY_FRACTION:
      return (
        <HintModalVideo
          title="Here is how to simplify a fraction"
          urls={fractionHintVideoUrls}
          analyticsVideoName={`hint-${HintType.HINT_SIMPLIFY_FRACTION}`}
        />
      );
    case HintType.HINT_BEARINGS:
      return <HintModalImageAndText image={Bearings3DigitSupport} altText="Bearings calculation" />;
    case HintType.HINT_MONEY:
      return <HintModalImageAndText image={Money2dpSupport} altText="Money calculation" />;
    case HintType.HINT_TARGETED_SUPPORT:
      if (support?.videoId) {
        return (
          <HintModalVideo
            title={support.text}
            urls={makeQuestionVideoUrls(support.videoId)}
            analyticsVideoName={`hint-${HintType.HINT_TARGETED_SUPPORT}`}
          />
        );
      } else if (support?.text || support?.imageId) {
        return <HintModalImageAndText text={support.text} image={getImageURL(support.imageId)} />;
      }
      return null;
    default:
      return null;
  }
};

type HintModalVideoProps = {
  title?: string;
  urls: VideoUrls;
  analyticsVideoName: string;
};

const HintModalVideo = ({ title, urls, analyticsVideoName }: HintModalVideoProps) => (
  <>
    {title && <Dialog.Title className={dialogStyles.DialogTitle}>{title}</Dialog.Title>}
    <div className={styles.HintVideoContainer}>
      <Video
        videoUrl={urls.videoUrl}
        posterUrl={urls.posterUrl}
        subtitleUrl={urls.subtitleUrl}
        analyticsCategory="question"
        analyticsVideoName={analyticsVideoName}
      />
    </div>
  </>
);

type HintModalImageAndTextProps = {
  text?: string;
  image?: string;
  altText?: string;
};

const HintModalImageAndText = ({ text, image, altText }: HintModalImageAndTextProps) => (
  <>
    {text && <Dialog.Title className={dialogStyles.DialogTitle}>{text}</Dialog.Title>}
    {image && (
      <div className={styles.HintContentContainer}>
        <img src={image} alt={altText} />
      </div>
    )}
  </>
);

HintModalContent.displayName = 'HintModalContent';
